import { IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { GeofenceType } from "@enums/GeofenceType";
import { IGeofence } from "@interfaces/IGeofences";
import { GeofenceService } from "@services/geofence.service";
import * as angular from "angular";
import { CsvImporterService } from "../csvImporter.service";
import { IImportGeofence } from "../interfaces/IImportGeofence";
import { IImportOption } from "../interfaces/IImportOption";
import { IImportValidationError, IImportValidationStatus } from "../interfaces/IImportValidation";

export class GeofenceImporterService {

    static $inject = ["$q", "csvImporterService", "geofenceService"];

    constructor(private readonly $q,
        private readonly csvImporterService: CsvImporterService,
        private readonly geofenceService: GeofenceService) {
    }

    validateRow(csvLine: string, importOptions: IImportOption[], geofences: IGeofence[], rowIdx: number): IImportValidationStatus {
        const nameColIdx = importOptions[0].mappedColumnIdx;
        const cellValues = this.csvImporterService.getFieldValuesFromLine(csvLine);
        const geofenceName = (cellValues[nameColIdx] || "").toLowerCase();
       
        const isValid = geofences.find(g => g.Description.toLowerCase() === geofenceName) === undefined;

        const validationError: IImportValidationError = {
            rowNumber: rowIdx + 1,
            colNumber: (importOptions[0].mappedColumnIdx + 1).toString(),
            fieldName: "Geofence Name",
            fieldValue: geofenceName,
            text: "Geofence already exists"
        };

        const validationStatus: IImportValidationStatus = {
            isValid: isValid,
            error: isValid ? null : validationError
        }

        return validationStatus;
    }

    validateData(csvLines: string[], importOptions: IImportOption[], geofences: IGeofence[]): angular.IPromise<IVeriTableRow<IImportGeofence>[]> {
        const defer = this.$q.defer();

        const importGeofences = this.getGeofences(csvLines, importOptions, geofences);

        const tableRows = this.createTableRows(importGeofences);

        defer.resolve(tableRows);

        return defer.promise;
    }

    private getGeofences(csvLines: string[], importOptions: IImportOption[], geofences: IGeofence[]): IImportGeofence[] {

        const importGeofences: IImportGeofence[] = [];

        csvLines.forEach(csvLine => {

            const cellValues = this.csvImporterService.getFieldValuesFromLine(csvLine);

            const importGeofence = this.getGeofence(cellValues, importOptions, geofences);

            importGeofences.push(importGeofence);
        });

        return importGeofences;
    }

    private getGeofence(cellValues: string[], importOptions: IImportOption[], geofences: IGeofence[]): IImportGeofence {
        const descColIdx = importOptions[0].mappedColumnIdx;
        const extRefColIdx = importOptions[1].mappedColumnIdx;
        const address1ColIdx = importOptions[2].mappedColumnIdx;
        const address2ColIdx = importOptions[3].mappedColumnIdx;
        const address3ColIdx = importOptions[4].mappedColumnIdx;
        const address4ColIdx = importOptions[5].mappedColumnIdx;
        const postCodeColIdx = importOptions[6].mappedColumnIdx;
        const latColIdx = importOptions[7].mappedColumnIdx;
        const lngColIdx = importOptions[8].mappedColumnIdx;
        const radiusColIdx = importOptions[9].mappedColumnIdx;

        const address4 = this.getCellValue(cellValues, importOptions[5], address4ColIdx);
        const postCode = this.getCellValue(cellValues, importOptions[6], postCodeColIdx);
        const address4AndPostCode = address4 && postCode ? address4 + " " + postCode : address4 + postCode;

        const latText = this.getCellValue(cellValues, importOptions[7], latColIdx);
        const lngText = this.getCellValue(cellValues, importOptions[8], lngColIdx);

        const lat = +latText;
        const long = +lngText;
        const mapCoords = new google.maps.LatLng(lat, long);

        const geofence: IGeofence = {
            Description: this.getCellValue(cellValues, importOptions[0], descColIdx),
            DescriptionExt: this.getCellValue(cellValues, importOptions[1], extRefColIdx),
            Addr1: this.getCellValue(cellValues, importOptions[2], address1ColIdx),
            Addr2: this.getCellValue(cellValues, importOptions[3], address2ColIdx),
            Addr3: this.getCellValue(cellValues, importOptions[4], address3ColIdx),
            Addr4: address4AndPostCode,
            MapCoords: mapCoords,
            Radius: this.getCellValue(cellValues, importOptions[9], radiusColIdx),
            Latitude: lat,
            Longitude: long,
            GeofenceType: GeofenceType.Circle   // currently polygons not supported
        };

        const importGeofence: IImportGeofence = {
            geofence: geofence,
            name: geofence.Description,
            failedImport: false,
            successfullyImported: false,
            isValid: true,
            latText: latText,
            lngText: lngText,
            errors: []
        }

        return importGeofence;
    }

    getCellValue(cellValues: string[], importOption: IImportOption, colIdx: number) {
        if (colIdx < 0) {
            return importOption.defaultValue ? importOption.defaultValue : "";
        }
        let importValue = cellValues[colIdx];
        if (!importValue && importOption.isRequired && importOption.defaultValue) {
            importValue = importOption.defaultValue;
        }
        return importValue;
    }

    private createTableRows(geofences: IImportGeofence[]): IVeriTableRow<IImportGeofence>[] {
        const tableRows = [] as IVeriTableRow<IImportGeofence>[];

        geofences.forEach((geofence, rowIdx) => {
            let rowClass = "";

            if (!geofence.isValid || geofence.failedImport) {
                rowClass += "invalid";
            }
            if (geofence.isValid) {
                rowClass += "success";
            }

            tableRows.push({
                rowClassName: rowClass,
                data: geofence,
                cells: this.createTableCells(geofence, rowIdx + 1)
            });
        });

        return tableRows;
    }

    private createTableCells(importGeofence: IImportGeofence, rowNumber: number): IVeriTableRowCell[] {
        const geofence = importGeofence.geofence;
        const cells: IVeriTableRowCell[] = [
            {
                contents: rowNumber.toString()
            },
            {
                contents: importGeofence.failedImport ? "Failed to import" : geofence.Description,
                className: "bold"
            },
            {
                contents: geofence.DescriptionExt,
                sortData: geofence.DescriptionExt
            },
            {
                contents: geofence.Addr1,
                sortData: geofence.Addr1
            },
            {
                contents: geofence.Addr4,
                sortData: geofence.Addr4
            },
            {
                contents: importGeofence.latText ? importGeofence.latText : ""
            },
            {
                contents: importGeofence.lngText ? importGeofence.lngText : ""
            }
        ];

        return cells;
    }
}

